/**
 * <warranty-addon>
 * Product-page extended-warranty upsell (HME Care) — fixed-price bands.
 *
 * Computes RRP x % (from the selected option), snaps to the nearest FIXED price
 * band, and adds the matching "HME Care Extended Warranty" variant to the cart at
 * quantity 1 — right after the main product is added, bound to it via a
 * _parent_id property. Because the band is a real fixed-price variant, the price
 * can't be edited down; because it's only ever added alongside the product (and
 * cart.js removes orphans), it can't be bought on its own.
 *
 * Band lookup: window.HME_WARRANTY_BANDS = { priceInPence: variantId }.
 */
if (!customElements.get('warranty-addon')) {
  customElements.define(
    'warranty-addon',
    class WarrantyAddon extends HTMLElement {
      connectedCallback() {
        this.formId = this.dataset.formId;
        this.form = this.formId ? document.getElementById(this.formId) : null;
        this.radios = Array.from(this.querySelectorAll('input[type="radio"]'));
        this.pending = null;

        // Sorted band prices (pence) for nearest-band snapping.
        const bands = window.HME_WARRANTY_BANDS || {};
        this.bands = bands;
        this.bandKeys = Object.keys(bands)
          .map((k) => parseInt(k, 10))
          .filter((n) => !isNaN(n))
          .sort((a, b) => a - b);

        this.onRadioChange = this.onRadioChange.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.onCartAdded = this.onCartAdded.bind(this);
        this.onVariantError = this.onVariantError.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);

        this.addEventListener('change', this.onRadioChange);
        if (this.form) this.form.addEventListener('submit', this.onFormSubmit);
        document.addEventListener('cart:item-added', this.onCartAdded);
        document.addEventListener('product:variant-error', this.onVariantError);
        document.addEventListener('variant:changed', this.onVariantChange);
        document.addEventListener('variant:change', this.onVariantChange);

        this.computeBands();
      }

      disconnectedCallback() {
        document.removeEventListener('cart:item-added', this.onCartAdded);
        document.removeEventListener('product:variant-error', this.onVariantError);
        document.removeEventListener('variant:changed', this.onVariantChange);
        document.removeEventListener('variant:change', this.onVariantChange);
      }

      get selected() {
        return this.radios.find((r) => r.checked) || null;
      }

      /** Nearest fixed band price (pence) to an ideal amount; caps at the ends. */
      snap(pence) {
        if (!this.bandKeys.length) return null;
        let best = this.bandKeys[0];
        let bestDiff = Math.abs(pence - best);
        for (const k of this.bandKeys) {
          const d = Math.abs(pence - k);
          if (d < bestDiff) {
            best = k;
            bestDiff = d;
          }
        }
        return best;
      }

      /** For each paid option, snap RRP x % to a band and record its variant id. */
      computeBands() {
        const base = parseInt(this.dataset.basePrice || '0', 10); // pence
        this.radios.forEach((radio) => {
          const pct = parseFloat(radio.dataset.pct || '0');
          if (!pct) return;
          const ideal = Math.round((base * pct) / 100);
          const bandPence = this.snap(ideal);
          if (bandPence == null) return;
          radio.dataset.bandPrice = bandPence;
          radio.dataset.variantId = this.bands[bandPence] || this.bands[String(bandPence)] || '';
          const display = radio.closest('.product-warranty-addon__option')?.querySelector('[data-price-display]');
          if (display) display.textContent = this.formatMoney(bandPence);
        });
        this.syncSelectedClass();
      }

      onRadioChange() {
        this.syncSelectedClass();
      }

      syncSelectedClass() {
        const radio = this.selected;
        this.querySelectorAll('.product-warranty-addon__option').forEach((li) => {
          li.classList.toggle('is-selected', !!radio && li.contains(radio));
        });
      }

      onVariantChange(event) {
        const variant = event && event.detail ? event.detail.variant : null;
        if (!variant || typeof variant.price === 'undefined') return;
        this.dataset.basePrice = variant.price; // pence
        this.computeBands();
      }

      /** Record that this form was submitted with a paid warranty option. */
      onFormSubmit() {
        const radio = this.selected;
        const years = radio ? parseInt(radio.dataset.years || '0', 10) : 0;
        const variantId = radio ? radio.dataset.variantId : '';
        if (years <= 0 || !variantId) {
          this.pending = null;
          return;
        }
        const mainVariantId = this.form?.querySelector('[name="id"]')?.value;
        this.pending = {
          mainVariantId: mainVariantId ? String(mainVariantId) : null,
          variantId: Number(variantId),
          label: radio.dataset.label || '',
        };
      }

      onVariantError() {
        this.pending = null;
      }

      /** After the main product is added, add its warranty band as a bound line. */
      onCartAdded(event) {
        if (!this.pending) return;
        const added = event && event.detail ? event.detail.product : null;
        const addedId = added ? String(added.variant_id || added.id) : null;
        if (this.pending.mainVariantId && addedId && addedId !== this.pending.mainVariantId) return;

        const job = this.pending;
        this.pending = null;
        if (!job.variantId) return;

        const brand = (this.dataset.coverBrand || '').trim();
        const properties = {
          Cover: brand ? brand + ' — ' + job.label : job.label,
          'For product': this.dataset.productTitle || '',
          _warranty: '1',
          _parent_id: job.mainVariantId || '',
        };
        if (this.dataset.variantSku) properties.SKU = this.dataset.variantSku;

        fetch(`${window.theme?.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: job.variantId, quantity: 1, properties }] }),
        })
          .then((r) => r.json())
          .then(() => document.dispatchEvent(new CustomEvent('cart:refresh')))
          .catch((e) => console.error('[warranty-addon] add failed', e));
      }

      formatMoney(pence) {
        if (window.theme && typeof window.theme.moneyFormat === 'string' && window.Shopify && Shopify.formatMoney) {
          return Shopify.formatMoney(pence, window.theme.moneyFormat);
        }
        return '£' + (pence / 100).toFixed(2);
      }
    }
  );
}
